import type { ClockState, StaffDayEntry } from './api.ts';
import { hoursText } from './staff-display.ts';

/** Ton des Zustands-Chips (wie `StateChipTone` in components/ui/state-chip.tsx). */
export type ClockChipTone = 'ready' | 'check' | 'neutral';

/**
 * Zustand und nächster Schritt der Stempel-Karte (Ticket 1.284 Nacharbeit) – rein, ohne Uhr und Netz, nur Anzeige: Texte
 * und Auswahl aus Zustand, Stempeln (Felder `from`/`to`) und Minuten des Servers. Keine Hinweise zum Arbeitszeitgesetz
 * [RUDOLF 22.10.2026].
 */
export const CLOCK_STEP_TEXTS = {
  presentSince: (time: string) => `da seit ${time}`,
  present: 'da',
  breakSince: (time: string) => `in Pause seit ${time}`,
  onBreak: 'in Pause',
  leftAt: (time: string) => `gegangen ${time}`,
  absent: 'nicht da',
  /** Nächster Schritt (Hinweis-Karte, nur Zeiterfassung › Heute; nie Arbeitszeitgesetz [RUDOLF 22.10.2026]). */
  notStamped: 'Noch nicht gestempelt · Kommen',
  notStampedText: (target: string | null) => `${target ? `Soll heute ${target}. ` : ''}Ein Tipp auf „Kommen“ beginnt den Tag.`,
} as const;

/** Zustands-Chip aus Zustand und Stempeln des Tages (Uhrzeiten aus den Feldern `from`, keine Satz-Auswertung). */
export function clockStateChip(state: ClockState, entries: readonly StaffDayEntry[]): { tone: ClockChipTone; text: string } {
  const last = (pick: (e: StaffDayEntry) => boolean) => [...entries].reverse().find(pick);
  if (state === 'present') {
    const since = last((e) => e.type === 'clock_in');
    return { tone: 'ready', text: since ? CLOCK_STEP_TEXTS.presentSince(since.from) : CLOCK_STEP_TEXTS.present };
  }
  if (state === 'on_break') {
    const since = last((e) => e.type === 'break' && e.to === null);
    return { tone: 'check', text: since ? CLOCK_STEP_TEXTS.breakSince(since.from) : CLOCK_STEP_TEXTS.onBreak };
  }
  const left = last((e) => e.type === 'clock_out');
  return { tone: 'neutral', text: left ? CLOCK_STEP_TEXTS.leftAt(left.from) : CLOCK_STEP_TEXTS.absent };
}

/**
 * Nächster Schritt der Stempel-Karte (rein, nur Wiedergabe der Server-Werte, keine Prognose): nicht gestempelt → „Noch
 * nicht gestempelt · Kommen“. Sonst nichts: „heute noch 3:48 h bis Soll“ steht schon im Satz des Servers (Story 22.11,
 * Sichtung HS54 29.10.2026: nicht doppelt).
 */
export function clockNextStep(
  state: ClockState,
  day: { entries: readonly StaffDayEntry[]; targetMinutes: number; balanceMinutes: number },
): { title: string; text: string } | null {
  if (state === 'absent' && day.entries.length === 0) {
    return { title: CLOCK_STEP_TEXTS.notStamped, text: CLOCK_STEP_TEXTS.notStampedText(day.targetMinutes > 0 ? hoursText(day.targetMinutes) : null) };
  }
  return null;
}
